import React, { useMemo } from 'react';

import DOMPurify from 'dompurify';
import { marked } from 'marked';

import { Box, BoxProps } from '@mui/material';
import { useTheme } from '@mui/material/styles';

type Props = {
  content: string;
  boxProps?: BoxProps;
};

export default function MarkdownPreview({ content, boxProps }: Props) {
  const theme = useTheme();
  const html = DOMPurify.sanitize(marked.parse(content, { async: false }));
  const baseSx = useMemo(
    () => ({
      color: 'text.primary',
      '& code': {
        backgroundColor: 'action.hover',
        color: 'text.primary',
        padding: '0.2em 0.5em',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        fontFamily: 'monospace',
        fontSize: '0.875em',
      },
      '& pre': {
        backgroundColor: 'background.default',
        color: 'text.primary',
        padding: 2,
        borderRadius: 1,
        overflow: 'auto',
        '& code': {
          backgroundColor: 'transparent',
          padding: 0,
          border: 'none',
          borderRadius: 0,
        },
      },
      '& blockquote': {
        borderLeft: `4px solid ${theme.palette.divider}`,
        color: 'text.secondary',
        margin: 0,
        paddingLeft: 2,
      },
      '& table': {
        borderCollapse: 'collapse',
        width: '100%',
        '& th, & td': {
          border: `1px solid ${theme.palette.divider}`,
          padding: 1,
        },
        '& th': {
          backgroundColor: 'action.hover',
        },
      },
    }),
    [theme.palette.divider],
  );
  const { sx: boxSx, ...restBoxProps } = boxProps ?? {};

  return (
    <Box
      dangerouslySetInnerHTML={{ __html: html }}
      sx={[baseSx, ...(Array.isArray(boxSx) ? boxSx : boxSx ? [boxSx] : [])]}
      {...restBoxProps}
    />
  );
}
